import { httpsCallable } from 'firebase/functions'

import { functions } from '../../../firebase'

// Type definitions for the cloud function responses
interface ReindexResponse {
  success: boolean
  data?: {
    count: number
    indexName: string
    processingTimeMs: number
  }
  error?: string
}

interface ConfigureIndexResponse {
  success: boolean
  data?: {
    indexName: string
    processingTimeMs: number
  }
  error?: string
}

// Create callable function instances
const reindexGifsFunction = httpsCallable<void, ReindexResponse>(
  functions,
  'reindexGifs'
)
const configureAlgoliaIndexFunction = httpsCallable<
  void,
  ConfigureIndexResponse
>(functions, 'configureAlgoliaIndex')

export const AlgoliaService = {
  /**
   * Trigger a full reindex of all GIF documents from Firestore to Algolia
   * This clears the existing index and re-uploads all documents with proper configuration
   */
  async reindexGifs(): Promise<{
    success: boolean
    data?: {
      count: number
      indexName: string
      processingTimeMs: number
    }
    error?: string
  }> {
    try {
      const result = await reindexGifsFunction()
      return result.data
    } catch (error) {
      console.error('Error calling reindexGifs function:', error)
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Failed to reindex GIFs',
      }
    }
  },

  /**
   * Configure Algolia index settings only (without reindexing data)
   * Updates searchable attributes, facets, and other search configuration
   */
  async configureIndex(): Promise<{
    success: boolean
    data?: {
      indexName: string
      processingTimeMs: number
    }
    error?: string
  }> {
    try {
      const result = await configureAlgoliaIndexFunction()
      return result.data
    } catch (error) {
      console.error('Error calling configureAlgoliaIndex function:', error)
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to configure Algolia index',
      }
    }
  },

  /**
   * Format processing time for display
   */
  formatProcessingTime(ms: number): string {
    if (ms < 1000) {
      return `${ms}ms`
    } else if (ms < 60000) {
      return `${(ms / 1000).toFixed(1)}s`
    } else {
      return `${(ms / 60000).toFixed(1)}m`
    }
  },
}
